import { useState } from "react";
import { Button } from "../../design-system/Button";
import { Icon } from "../../design-system/Icon";
import { Badge } from "../../design-system/Badge";
import { LoadingState, ErrorState } from "../../components/States";
import { useIpc, useIpcQuery } from "../../ipc/IpcProvider";
import { MlConnectPanel, ProviderForm, AI_PROVIDER_FIELDS } from "../security/CredentialsScreen";
import type { CredentialsStatus, OnboardingStatus } from "../../ipc/types";
import "../product-development/product-development.css";

/** Pasos del asistente de primer arranque. */
type StepId = "welcome" | "ml" | "ai" | "done";
const STEPS: readonly StepId[] = ["welcome", "ml", "ai", "done"];
const STEP_TITLES: Record<StepId, string> = {
  welcome: "Te damos la bienvenida",
  ml: "Conecta Mercado Libre",
  ai: "Conecta la Inteligencia Artificial",
  done: "¡Todo listo!",
};

/**
 * Asistente de primer arranque (item 2 de los últimos retoques). Guía al usuario a conectar
 * Mercado Libre (OAuth real) y su proveedor de IA, con "Probar conexión" en cada paso. Se muestra
 * como overlay hasta que el usuario lo completa u omite; entonces no vuelve a aparecer.
 */
export function OnboardingWizard({ onFinish }: { onFinish: () => void }): JSX.Element {
  const client = useIpc();
  const { state, reload } = useIpcQuery<CredentialsStatus>((c) => c.getCredentialsStatus(), []);
  const [stepIdx, setStepIdx] = useState(0);
  const [closing, setClosing] = useState(false);
  const step = STEPS[stepIdx]!;

  const finish = async (): Promise<void> => {
    setClosing(true);
    await client.completeOnboarding();
    onFinish();
  };
  const next = (): void => setStepIdx((i) => Math.min(i + 1, STEPS.length - 1));
  const back = (): void => setStepIdx((i) => Math.max(i - 1, 0));

  const s = state.status === "ready" ? state.data : null;
  const mlOk = s?.mercadolibre.configured ?? false;
  const aiOk = s?.ai.configured ?? false;

  return (
    <div
      role="presentation"
      style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1100, padding: "var(--sp-4)" }}
    >
      <div
        role="dialog" aria-modal="true" aria-label={STEP_TITLES[step]}
        style={{ background: "var(--bg-surface)", border: "1px solid var(--border)", borderRadius: "var(--radius-lg, 14px)", maxWidth: 720, width: "100%", maxHeight: "90vh", overflowY: "auto", boxShadow: "0 24px 70px rgba(0,0,0,0.45)" }}
      >
        {/* Cabecera con progreso */}
        <div style={{ padding: "var(--sp-5)", borderBottom: "1px solid var(--border)" }}>
          <div className="row" style={{ gap: 10, marginBottom: "var(--sp-3)" }}>
            <div className="sidebar__logo" style={{ width: 36, height: 36 }}><Icon name="spark" size={18} /></div>
            <div>
              <h2 className="strong" style={{ fontSize: 17 }}>{STEP_TITLES[step]}</h2>
              <span className="faint" style={{ fontSize: 12.5 }}>Paso {stepIdx + 1} de {STEPS.length}</span>
            </div>
          </div>
          <div className="row" style={{ gap: 6 }}>
            {STEPS.map((id, i) => (
              <span key={id} style={{ flex: 1, height: 4, borderRadius: 2, background: i <= stepIdx ? "var(--brand)" : "var(--border)" }} />
            ))}
          </div>
        </div>

        {/* Cuerpo del paso */}
        <div style={{ padding: "var(--sp-5)" }}>
          {step === "welcome" ? (
            <div>
              <p className="muted" style={{ marginTop: 0, fontSize: 14, lineHeight: 1.6 }}>
                <b className="strong">FABIAN-MELI</b> es tu centro de operaciones para vender en Mercado Libre. Antes de empezar,
                conectemos tu cuenta y tu asistente de IA. Toma un par de minutos y puedes omitirlo si prefieres hacerlo después.
              </p>
              <ul className="muted" style={{ fontSize: 13.5, lineHeight: 1.8, paddingLeft: "1.1rem" }}>
                <li>Tus llaves se guardan <b className="strong">cifradas en tu equipo</b> (nunca se comparten).</li>
                <li>El inicio de sesión de Mercado Libre ocurre en <b className="strong">tu navegador</b>.</li>
                <li>Todo esto también está luego en <b className="strong">Seguridad → Credenciales</b>.</li>
              </ul>
            </div>
          ) : null}

          {step === "ml" ? (
            state.status === "loading" ? <LoadingState /> :
            state.status === "error" ? <ErrorState message={state.error.message} onRetry={reload} /> :
            <MlConnectPanel configured={mlOk} masked={s?.mercadolibre.masked ?? ""} onSaved={reload} />
          ) : null}

          {step === "ai" ? (
            state.status === "loading" ? <LoadingState /> :
            state.status === "error" ? <ErrorState message={state.error.message} onRetry={reload} /> :
            <ProviderForm
              provider="ai" title="Inteligencia Artificial" subtitle="API para las funciones de IA (OpenAI / Anthropic)" icon="ai"
              fields={AI_PROVIDER_FIELDS} configured={aiOk} masked={s?.ai.masked ?? ""} {...(s?.ai.extra ? { extraProvider: s.ai.extra } : {})} onSaved={reload}
            />
          ) : null}

          {step === "done" ? (
            <div>
              <p className="muted" style={{ marginTop: 0, fontSize: 14, lineHeight: 1.6 }}>Resumen de tu configuración:</p>
              <div className="pd-sim__result" style={{ marginBottom: "var(--sp-3)" }}>
                <Badge tone={mlOk ? "success" : "warning"} dot>{mlOk ? "Conectado" : "Pendiente"}</Badge>
                <span className="muted" style={{ fontSize: 13.5 }}>Mercado Libre {mlOk ? "quedó conectado." : "aún sin conectar (puedes hacerlo luego en Seguridad)."}</span>
              </div>
              <div className="pd-sim__result">
                <Badge tone={aiOk ? "success" : "warning"} dot>{aiOk ? "Configurado" : "Pendiente"}</Badge>
                <span className="muted" style={{ fontSize: 13.5 }}>Inteligencia Artificial {aiOk ? "quedó configurada." : "aún sin configurar (opcional)."}</span>
              </div>
              {mlOk ? (
                <div style={{ marginTop: "var(--sp-4)", padding: "var(--sp-4)", border: "1px solid var(--border)", borderRadius: "var(--radius-3, 12px)" }}>
                  <div className="strong" style={{ fontSize: 13.5, marginBottom: "var(--sp-2)" }}>Próximos pasos (en la pantalla Marketplace):</div>
                  <ul className="muted" style={{ fontSize: 13, lineHeight: 1.8, margin: 0, paddingLeft: "1.1rem" }}>
                    <li>Pulsa <b className="strong">"Traer publicaciones de ML"</b> para ver tus publicaciones reales en el catálogo.</li>
                    <li>Pulsa <b className="strong">"Traer órdenes de ML"</b> cuando tengas ventas nuevas por importar.</li>
                    <li>En <b className="strong">"Preguntas de compradores"</b>, la IA redacta respuestas con tus datos.</li>
                  </ul>
                </div>
              ) : null}
              <p className="muted" style={{ fontSize: 13.5, marginTop: "var(--sp-4)" }}>Ya puedes empezar a trabajar. Pulsa <b className="strong">Empezar</b> para entrar a tu panel.</p>
            </div>
          ) : null}
        </div>

        {/* Pie de navegación */}
        <div className="row" style={{ gap: 8, justifyContent: "space-between", padding: "var(--sp-4) var(--sp-5)", borderTop: "1px solid var(--border)" }}>
          <Button variant="ghost" size="sm" disabled={closing} onClick={finish}>Omitir por ahora</Button>
          <div className="row" style={{ gap: 8 }}>
            {stepIdx > 0 ? <Button variant="secondary" size="sm" disabled={closing} onClick={back}>Atrás</Button> : null}
            {step === "done"
              ? <Button size="sm" icon="check" disabled={closing} onClick={finish}>{closing ? "Entrando…" : "Empezar"}</Button>
              : <Button size="sm" icon="bolt" disabled={closing} onClick={next}>Siguiente</Button>}
          </div>
        </div>
      </div>
    </div>
  );
}

/** Puerta del asistente: consulta si ya se completó; si no, lo muestra sobre la app. */
export function OnboardingGate(): JSX.Element | null {
  const { state, reload } = useIpcQuery<OnboardingStatus>((c) => c.getOnboardingStatus(), []);
  if (state.status !== "ready" || state.data.done) return null;
  return <OnboardingWizard onFinish={reload} />;
}
